import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
  updateFrame,
  type AssetFrameResponse,
  type AssetResponse,
  type CopyElement,
  type UpdateFrameRequest,
} from '../../api';
import { FRAME_COPY_OPTIONS } from '../../constants/taxonomy';
import { SourceImageSelect, useSourceImageOptions } from '../grid/SourceImagePicker';
import { CopyElementsEditor } from '../metadata/CopyElementsEditor';
import { describeError } from '../../describeError';
import { useFocusWhenRendered } from '../metadata/useFocusWhenRendered';
import { useFrameUrls } from './useFrameUrls';

/**
 * Edit an uploaded carousel's frames: each frame's source image and copy.
 *
 * The same two controls the upload's details step gives each frame
 * (`SourceImageSelect`, `CopyElementsEditor` with the frame copy types), and
 * the same rules, enforced by `PATCH /assets/{id}/frames/{frame_id}`. One frame
 * open at a time, each saved on its own: a carousel's frames are independent,
 * and a rail that held six source pickers open at once would be unusable.
 *
 * Read-only exactly when the metadata form is, and the same on the Staged and
 * Published tabs.
 */

/** "Open this frame and put me in its copy", from the stage's caption. */
export interface FrameEditRequest {
  frameId: string;
  /** Bumped on every request, so asking twice for one frame still lands. */
  nonce: number;
}

interface CarouselFramesEditorProps {
  asset: AssetResponse;
  readOnly: boolean;
  /** The frame the stage is showing, marked so the two read as one. */
  activeFrameId?: string;
  /** Which row is open, when the overlay keeps it in step with the stage. */
  openFrameId?: string | null;
  onOpenFrameChange?: (frameId: string | null) => void;
  editRequest?: FrameEditRequest | null;
  /**
   * Whether any frame has an unsaved draft or a save in flight. The overlay
   * holds Publish on it, so a frame edit is never published past or lost.
   */
  onBusyChange?: (busy: boolean) => void;
}

export function CarouselFramesEditor({
  asset,
  readOnly,
  activeFrameId,
  openFrameId,
  onOpenFrameChange,
  editRequest,
  onBusyChange,
}: CarouselFramesEditorProps) {
  const frames = asset.frames ?? [];
  const [ownOpenId, setOwnOpenId] = useState<string | null>(null);
  const openId = openFrameId !== undefined ? openFrameId : ownOpenId;
  const setOpenId = (frameId: string | null) => {
    setOwnOpenId(frameId);
    onOpenFrameChange?.(frameId);
  };

  // Which frames are busy, collected from the rows into one flag upstream.
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const reportBusy = useCallback((frameId: string, busy: boolean) => {
    setBusyIds((current) => {
      if (current.has(frameId) === busy) return current;
      const next = new Set(current);
      if (busy) next.add(frameId);
      else next.delete(frameId);
      return next;
    });
  }, []);
  const anyBusy = busyIds.size > 0;
  // The cleanup reports "not busy" as the editor goes away: the overlay stays
  // mounted when it moves to an asset with no frames, and would otherwise hold
  // Publish on a draft that no longer exists.
  useEffect(() => {
    onBusyChange?.(anyBusy);
    return () => onBusyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyBusy]);
  const { urlById } = useFrameUrls(asset.id);
  // Every frame's current source is offered even if it is no longer published,
  // as the source section does, so a frame never reads as unlinked.
  const { options } = useSourceImageOptions(
    !readOnly,
    frames.flatMap((frame) => (frame.source_image_id ? [frame.source_image_id] : [])),
  );

  return (
    <ol className="flex flex-col gap-1.5">
      {frames.map((frame, position) => (
        <FrameRow
          key={frame.id}
          asset={asset}
          frame={frame}
          position={position}
          thumbUrl={urlById.get(frame.id)}
          isOpen={openId === frame.id}
          isActive={activeFrameId === frame.id}
          editRequest={editRequest?.frameId === frame.id ? editRequest : null}
          onBusyChange={reportBusy}
          onToggle={() => setOpenId(openId === frame.id ? null : frame.id)}
          readOnly={readOnly}
          sourceOptions={options}
        />
      ))}
    </ol>
  );
}

/** The two things a frame row edits, as saved. */
interface FrameValues {
  sourceId: string;
  copy: CopyElement[];
}

const valuesOf = (frame: AssetFrameResponse): FrameValues => ({
  sourceId: frame.source_image_id ?? '',
  copy: frame.copy_elements ?? [],
});

const sameValues = (a: FrameValues, b: FrameValues) =>
  a.sourceId === b.sourceId && JSON.stringify(a.copy) === JSON.stringify(b.copy);

/** Copy as it is sent: trimmed, blank rows dropped. */
const trimCopy = (copy: readonly CopyElement[]) =>
  copy
    .map((row) => ({ type: row.type, value: row.value.trim() }))
    .filter((row) => row.value !== '');

interface FrameRowProps {
  asset: AssetResponse;
  frame: AssetFrameResponse;
  position: number;
  thumbUrl: string | undefined;
  isOpen: boolean;
  isActive: boolean;
  editRequest: FrameEditRequest | null;
  onBusyChange: (frameId: string, busy: boolean) => void;
  onToggle: () => void;
  readOnly: boolean;
  sourceOptions: AssetResponse[];
}

function FrameRow({
  asset,
  frame,
  position,
  thumbUrl,
  isOpen,
  isActive,
  editRequest,
  onBusyChange,
  onToggle,
  readOnly,
  sourceOptions,
}: FrameRowProps) {
  const rowRef = useRef<HTMLLIElement>(null);
  const queryClient = useQueryClient();

  // What is saved, and what the user has changed, kept apart. The saved
  // baseline follows the data: the props, or the save's own response until a
  // refetch catches up, so a row never shows a value older than the one it
  // just saved. A draft is null until the user touches that field, so fresh
  // data reaches every field they have not touched.
  const [confirmed, setConfirmed] = useState<FrameValues | null>(null);
  const fromProps = useMemo(() => valuesOf(frame), [frame]);
  const saved = confirmed ?? fromProps;
  useEffect(() => {
    // The refetch has caught up with the save; follow the data again.
    if (confirmed && sameValues(confirmed, fromProps)) setConfirmed(null);
  }, [confirmed, fromProps]);

  const [sourceDraft, setSourceDraft] = useState<string | null>(null);
  const [copyDraft, setCopyDraft] = useState<CopyElement[] | null>(null);
  const sourceId = sourceDraft ?? saved.sourceId;
  const copy = copyDraft ?? saved.copy;

  const trimmed = trimCopy(copy);
  const sourceChanged = sourceId !== saved.sourceId;
  const copyChanged = JSON.stringify(trimmed) !== JSON.stringify(saved.copy);
  const isDirty = sourceChanged || copyChanged;

  const reset = () => {
    setSourceDraft(null);
    setCopyDraft(null);
  };

  const save = useMutation({
    // The drafts as they were when sent, so success can tell whether the
    // user has typed more since: only an unchanged draft is cleared.
    mutationFn: (sent: { source: string | null; copy: CopyElement[] | null }) => {
      const body: UpdateFrameRequest = {};
      if (sourceChanged) body.source_image_id = sourceId;
      if (copyChanged) body.copy_elements = trimmed;
      return updateFrame(asset.id, frame.id, body).then((response) => ({ response, sent }));
    },
    onSuccess: ({ response, sent }) => {
      const updated = response.frames?.find((candidate) => candidate.id === frame.id);
      if (updated) setConfirmed(valuesOf(updated));
      setSourceDraft((current) => (current === sent.source ? null : current));
      setCopyDraft((current) => (current === sent.copy ? null : current));
      toast.success(`Frame ${position + 1} saved`);
      // The grid, this overlay and the source picker all read the asset.
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'asset'] });
    },
    onError: (error) => toast.error(describeError(error, `Frame ${position + 1} was not saved`)),
  });

  // Sent here from the stage's "Add copy" / "Edit": add a headline row to
  // type in when the frame has none, and put the cursor in it once it is
  // drawn. Keyed on the nonce so asking twice for the same frame still acts.
  const nonce = editRequest?.nonce;
  const focusCopy = useFocusWhenRendered(rowRef);
  useEffect(() => {
    if (nonce === undefined || readOnly) return;
    // A headline to type in only if there is no copy at all, drafted or saved.
    setCopyDraft((current) =>
      (current ?? saved.copy).length > 0 ? current : [{ type: 'headline', value: '' }],
    );
    focusCopy();
    // `focusCopy` is a fresh function each render and only sets a flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, readOnly]);

  // What the row reports up: a draft or a save in flight holds Publish.
  const isBusy = isDirty || save.isPending;
  useEffect(() => {
    onBusyChange(frame.id, isBusy);
  }, [onBusyChange, frame.id, isBusy]);

  const headline = saved.copy.find((row) => row.type === 'headline') ?? saved.copy[0];

  return (
    <li
      ref={rowRef}
      // The frame on the stage is the one marked here, in the stage's own
      // brand blue, so it is plain which row edits the picture on show.
      className={`overflow-hidden rounded-md border bg-white transition-shadow ${
        isActive ? 'border-[#0014cc]/40 shadow-[inset_3px_0_0_#0014cc]' : 'border-slate-200'
      }`}
    >
      <button
        type="button"
        onClick={() => {
          if (isOpen) reset();
          onToggle();
        }}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2.5 px-2 py-1.5 text-left hover:bg-slate-50"
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
        ) : (
          <div className="h-9 w-9 shrink-0 rounded bg-slate-100" />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[12px] font-medium text-slate-800">
            Frame {position + 1}
            <span className="ml-1.5 font-normal text-[#0014cc]">
              {frame.source_image_code ?? 'No source image'}
            </span>
          </span>
          <span className="truncate text-[11px] text-slate-400">
            {headline ? headline.value : 'No copy'}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          strokeWidth={2}
        />
      </button>

      {isOpen && (
        <div className="flex flex-col gap-3 border-t border-slate-100 px-2.5 py-3">
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Source image
            </span>
            {readOnly ? (
              <span className="text-[12px] text-slate-700">{frame.source_image_code ?? '—'}</span>
            ) : (
              <SourceImageSelect
                single
                required
                value={sourceId ? [sourceId] : []}
                // A frame is always cut from one: picking replaces it, and
                // unpicking keeps the current one rather than leaving none.
                onChange={(ids) => setSourceDraft(ids[0] ?? sourceId)}
                options={sourceOptions}
                aria-label={`Source image for frame ${position + 1}`}
              />
            )}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Copy
            </span>
            <CopyElementsEditor
              value={copy}
              onChange={setCopyDraft}
              readOnly={readOnly}
              typeOptions={FRAME_COPY_OPTIONS}
            />
          </div>
          {!readOnly && (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={!isDirty || save.isPending}
                onClick={reset}
                className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!isDirty || save.isPending}
                onClick={() => save.mutate({ source: sourceDraft, copy: copyDraft })}
                className="flex items-center gap-1.5 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              >
                {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
